import { UUID } from "crypto";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/app/api";
import { productKeys } from "@/query/keys/products";

/**
 * Get children of a product
 */
export function useProductChildren(
  productId: UUID | null,
  recursive: boolean = false,
) {
  return useQuery({
    queryKey: ["product-relationships", "children", productId, recursive],
    queryFn: () => api.productRelationships.getChildren(productId!, recursive),
    enabled: !!productId,
  });
}

/**
 * Get parents of a product
 */
export function useProductParents(productId: UUID | null) {
  return useQuery({
    queryKey: ["product-relationships", "parents", productId],
    queryFn: () => api.productRelationships.getParents(productId!),
    enabled: !!productId,
  });
}

/**
 * Add a child product to a parent (create relationship)
 */
export function useAddProductChild() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ parentId, childId }: { parentId: UUID; childId: UUID }) =>
      api.productRelationships.addChild(parentId, childId),
    onSuccess: (_, variables) => {
      // Invalidate queries for both parent and child
      queryClient.invalidateQueries({
        queryKey: ["product-relationships", "children", variables.parentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["product-relationships", "parents", variables.childId],
      });
      // Also invalidate product lists
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

/**
 * Remove a child product from a parent (delete relationship)
 */
export function useRemoveProductChild() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ parentId, childId }: { parentId: UUID; childId: UUID }) =>
      api.productRelationships.removeChild(parentId, childId),
    onSuccess: (_, variables) => {
      // Invalidate queries for both parent and child
      queryClient.invalidateQueries({
        queryKey: ["product-relationships", "children", variables.parentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["product-relationships", "parents", variables.childId],
      });
      // Also invalidate product lists
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}
